import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/authStore';
import type { LoginFormData, RegisterFormData, ChangePasswordFormData, ProfileFormData } from '@/lib/validators';

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setAuth, clearAuth, updateUser, user, accessToken } = useAuthStore();

  const userQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await authService.getMe();
      updateUser(response.data);
      return response.data;
    },
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000,
  });

  const login = useMutation({
    mutationFn: (data: LoginFormData) => authService.login(data),
    onSuccess: (response) => {
      const { accessToken: token, refreshToken, user: userData } = response.data;
      setAuth(token, userData);
      localStorage.setItem('crm_refresh_token', refreshToken);
      toast.success('Login successful');
      navigate('/dashboard');
    },
    onError: () => {
      toast.error('Invalid email or password');
    },
  });

  const register = useMutation({
    mutationFn: (data: RegisterFormData) =>
      authService.register({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
      }),
    onSuccess: (response) => {
      const { accessToken: token, refreshToken, user: userData } = response.data;
      setAuth(token, userData);
      localStorage.setItem('crm_refresh_token', refreshToken);
      toast.success('Account created successfully');
      navigate('/dashboard');
    },
    onError: (error) => {
      // This route only creates the very first account; afterwards the server
      // returns 403 and explains that administrators create users. Show that
      // message — "please try again" would send people round in circles.
      const message = (error as AxiosError<{ message?: string | string[] }>)?.response?.data?.message;
      toast.error(
        (Array.isArray(message) ? message.join(', ') : message) ||
          'Registration failed. Please try again.',
      );
    },
  });

  const logout = useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      clearAuth();
      localStorage.removeItem('crm_refresh_token');
      queryClient.clear();
      navigate('/login');
    },
  });

  const updateProfile = useMutation({
    mutationFn: (data: ProfileFormData) => authService.updateMe(data),
    onSuccess: (response) => {
      updateUser(response.data);
      toast.success('Profile updated successfully');
    },
    onError: () => {
      toast.error('Failed to update profile');
    },
  });

  const changePassword = useMutation({
    mutationFn: (data: ChangePasswordFormData) =>
      authService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      }),
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: () => {
      toast.error('Failed to change password. Check your current password.');
    },
  });

  const refreshToken = useMutation({
    mutationFn: (data: { refreshToken: string }) =>
      authService.refresh(data.refreshToken),
    onSuccess: (response) => {
      if (user) setAuth(response.data.accessToken, user);
      localStorage.setItem('crm_refresh_token', response.data.refreshToken);
    },
  });

  return {
    user: userQuery.data ?? user,
    isLoading: userQuery.isLoading,
    isAuthenticated: !!accessToken,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
  };
}
